import {
  AdminPricingApiService,
  CABINET_DIALOG,
  CABINET_PATHS,
  todayMinskIsoDate,
  type AdminDomainPricingVersionDetailResponse,
  type PricingDomain,
  type PricingDomainConfigMap,
} from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  NotButton,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';
import { firstValueFrom } from 'rxjs';
import { formatPricingDate, formatPricingRate } from './staff-pricing.util';

@Component({
  selector: 'lib-staff-pricing-domain-detail',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, NotButton],
  templateUrl: './staff-pricing-domain-detail.component.html',
  styleUrl: './staff-pricing-domain-detail.component.scss',
})
export class StaffPricingDomainDetailComponent implements OnInit {
  readonly domain = input.required<PricingDomain>();
  readonly versionId = input<string | null>(null);

  readonly #pricingApi = inject(AdminPricingApiService);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #dialog = inject(CABINET_DIALOG);
  readonly #translate = inject(TranslateService);
  readonly #notification = inject(NotSystemNotificationService);

  protected readonly loading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly deleting = signal(false);
  protected readonly version = signal<
    AdminDomainPricingVersionDetailResponse<PricingDomain> | null
  >(null);
  protected readonly showRawJson = signal(false);

  protected readonly isScheduled = computed(() => {
    const detail = this.version();
    if (!detail) {
      return false;
    }
    return detail.effective_from > todayMinskIsoDate();
  });

  public ngOnInit(): void {
    void this.loadVersion();
  }

  protected toggleRawJson(): void {
    this.showRawJson.update((value) => !value);
  }

  protected onEdit(): void {
    const detail = this.version();
    if (!detail || !this.isScheduled()) {
      return;
    }
    void this.#router.navigate([
      this.#cabinetPaths.staff,
      'pricing',
      this.domain(),
      detail.id,
      'edit',
    ]);
  }

  protected async onDelete(): Promise<void> {
    const detail = this.version();
    if (!detail || !this.isScheduled() || this.deleting()) {
      return;
    }

    const confirmed = await this.#dialog.confirm({
      title: this.#translate.instant('admin.pricing.deleteConfirmTitle'),
      message: this.#translate.instant('admin.pricing.deleteConfirmMessage'),
      okLabel: this.#translate.instant('admin.pricing.actions.delete'),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
      destructive: true,
    });
    if (!confirmed) {
      return;
    }

    this.deleting.set(true);
    try {
      await firstValueFrom(
        this.#pricingApi.deleteVersion(this.domain(), detail.id),
      );
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [this.#translate.instant('admin.pricing.deleteSuccess')],
      });
      void this.#router.navigate([
        this.#cabinetPaths.staff,
        'pricing',
        this.domain(),
      ]);
    } catch (error) {
      console.error('Failed to delete pricing version:', error);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.pricing.deleteError')],
      });
    } finally {
      this.deleting.set(false);
    }
  }

  protected formatDate = formatPricingDate;
  protected formatRate = formatPricingRate;

  protected formattedConfig(): string {
    const config = this.version()?.config;
    if (!config) {
      return '';
    }
    return JSON.stringify(config, null, 2);
  }

  protected storageConfig(): PricingDomainConfigMap['storage'] | null {
    if (this.domain() !== 'storage') {
      return null;
    }
    return this.version()?.config as PricingDomainConfigMap['storage'];
  }

  protected movingConfig(): PricingDomainConfigMap['moving'] | null {
    if (this.domain() !== 'moving') {
      return null;
    }
    return this.version()?.config as PricingDomainConfigMap['moving'];
  }

  protected promocodesConfig(): PricingDomainConfigMap['promocodes'] | null {
    if (this.domain() !== 'promocodes') {
      return null;
    }
    return this.version()?.config as PricingDomainConfigMap['promocodes'];
  }

  private async loadVersion(): Promise<void> {
    const id = this.versionId() ?? this.#route.snapshot.paramMap.get('id');
    if (!id) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.loadError.set(false);
    try {
      const detail = await firstValueFrom(
        this.#pricingApi.getVersion(this.domain(), id),
      );
      this.version.set(detail);
    } catch (error) {
      console.error('Failed to load pricing version:', error);
      this.version.set(null);
      this.loadError.set(true);
    } finally {
      this.loading.set(false);
    }
  }
}
